import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {isShadowContributor} from "@template-editor/helpers/uv-shadow-map/light-proxies"

class ShadowMaterialLightingModel extends THREE.LightingModel {
    private totalShadow = TSL.float(0).toVar()
    private totalWeight = TSL.float(0).toVar()

    override direct(lightData: THREE.LightingModelDirectInput, _builder: THREE.NodeBuilder) {
        const {lightNode} = lightData
        if (!isShadowContributor(lightNode)) return

        const {shadow, weight} = lightNode.shadowContribution()
        this.totalShadow.addAssign(shadow)
        this.totalWeight.addAssign(weight)
    }

    override finish(builder: THREE.NodeBuilder) {
        const context = builder.getContext()

        const shadowMask = this.totalWeight.greaterThan(0.0).select(this.totalShadow.div(this.totalWeight), TSL.float(0.0))

        TSL.diffuseColor.assign(TSL.vec4(shadowMask, shadowMask, shadowMask, 1.0))

        if (!(context.outgoingLight instanceof THREE.Node)) throw new Error("outgoingLight is not a Node")
        ;(context.outgoingLight as THREE.Node<"vec3">).rgb.assign(TSL.diffuseColor.rgb) // TODO: Optimize LightsNode to avoid this assignment
    }
}

class RenderShadowMaterial extends THREE.NodeMaterial {
    constructor() {
        super()
        this.lights = true
    }

    override setupLightingModel() {
        return new ShadowMaterialLightingModel()
    }
}

export const getShadowMaterial = (
    side: THREE.Side,
    uvNode: THREE.AttributeNode<"vec2">,
    previousUVShadowMap: THREE.TextureNode,
    iteration: THREE.UniformNode<"float", number>,
    receiverJitterUV: THREE.Node<"vec2">,
) => {
    const material = new RenderShadowMaterial()

    const jitteredUv = uvNode.add(receiverJitterUV)

    material.vertexNode = TSL.vec4(TSL.sub(jitteredUv.flipY(), TSL.vec2(0.5)).mul(2), 1, 1)

    const resolve = TSL.Fn(() => {
        const result = TSL.vec4(0.0).toVar()

        const current = TSL.output

        TSL.If(iteration.greaterThan(0.0), () => {
            const historyColor = previousUVShadowMap.sample(jitteredUv)
            const blended = TSL.add(current, historyColor.mul(iteration)).div(TSL.add(1.0, iteration))
            result.assign(blended)
        }).Else(() => {
            result.assign(current)
        })

        return result
    })

    material.outputNode = resolve()
    material.side = side

    return material
}

export const getDummyMaterial = (side: THREE.Side) => {
    const material = new THREE.MeshBasicMaterial()
    material.side = side
    return material
}

export const getMaskMaterial = (side: THREE.Side, uvNode: THREE.AttributeNode<"vec2">) => {
    const material = new RenderShadowMaterial()

    const uv = uvNode.flipY()

    material.vertexNode = TSL.vec4(TSL.sub(uv, TSL.vec2(0.5)).mul(2), 1, 1)

    const resolve = TSL.Fn(() => {
        const result = TSL.vec4(1.0)
        return result
    })

    material.outputNode = resolve()
    material.side = side

    return material
}
